import {
  Autocomplete,
  Box,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import type { FieldProps, FormikHelpers } from 'formik';
import { Field, FieldArray, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string } from 'yup';
import type { Flags, Schema } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import { OneOfFieldWatcher } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';
import RoundedButton from '@components/RoundedButton/index';

import { SORTED_FAMILY_RELATIONSHIP_OPTIONS } from '@constants/option-types/family-history';
import type { CategoryType } from '@constants/option-types/family-history';
import * as ROUTES from '@constants/routes';

import {
  useCreateFamilyHistory,
  useUpdateFamilyHistory,
} from '@hooks/api/family-history';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import type {
  FamilyHistoryRecord,
  FamilyHistoryValues,
  FamilyMemberCondition,
  FormValues,
} from '@models/family-history';
import { getFamilyHistoryTemplate } from '@models/fhir-templates/family-history';
import type { DrilldownProps } from '@models/record-drilldown';

import { capitalize } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';
import { dateNotBeforeSchema } from '@utils/validation';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import DrilldownWrapper from '../Common/DrilldownWrapper';
import FamilyConditionForm from './ConditionsForm';

const messages = defineMessages({
  description: {
    id: 'familyHistoryDrilldown.description',
    defaultMessage: 'Description',
  },
  relationship: {
    id: 'familyHistoryDrilldown.relationship',
    defaultMessage: 'Relationship',
  },
  deceased: {
    id: 'familyHistoryDrilldown.deceased',
    defaultMessage: 'Deceased',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date Of Birth',
  },
  deceasedDate: {
    id: 'familyHistoryDrilldown.deceasedDate',
    defaultMessage: 'Deceased Date',
  },
  deceasedBeforeBirth: {
    id: 'GLOBAL.dateValidation.deceasedBeforeBirth',
    defaultMessage: 'Deceased date cannot be before date of birth',
  },
  type: {
    id: 'GLOBAL.type',
    defaultMessage: 'Type',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  conditions: {
    id: 'familyHistoryDrilldown.conditions',
    defaultMessage: 'Conditions',
  },
  defaultTitle: {
    id: 'familyHistoryDrilldown.familyHistory',
    defaultMessage: 'Family History',
  },
  add: {
    id: 'familyHistoryDrilldown.addCondition',
    defaultMessage: 'Add Condition',
  },
});

/**
 * Age Diagnosed and Date Diagnosed now coexist (they no longer clear one
 * another). Both must stay nullable because FhirDateField emits `null` when
 * the curator clears Date Diagnosed, and Age Diagnosed is a plain optional
 * text field. Neither is required: a condition may be recorded with no
 * onset information at all.
 */
export function getFamilyConditionValidationSchema(requiredMessage: string) {
  return object().shape({
    name: string().required(requiredMessage),
    diagnosedYear: string().nullable(),
    ageDiagnosed: string().nullable(),
  });
}

interface FamilyHistoryDrilldownProps extends DrilldownProps<FamilyHistoryRecord> {
  /**
   * When true, suppresses the default redirect to the FAMILY_HISTORY route
   * after successfully creating a new family history record.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}
const FamilyHistory: React.FC<FamilyHistoryDrilldownProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const [createFamilyHistory, createFamilyHistoryLoading] =
    useCreateFamilyHistory();
  const [updateFamilyHistory, updateFamilyHistoryLoading] =
    useUpdateFamilyHistory();

  const loading =
    baseLoading ||
    createFamilyHistoryLoading ||
    updateFamilyHistoryLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    dateOfBirth: data?.dateOfBirth ?? '',
    deceasedDate: data?.deceasedDate ?? '',
    description: data?.description ?? '',
    isDeceased: data?.isDeceased ?? false,
    maternalPaternal: data?.maternalPaternal ?? '',
    relationshipCode: data?.relationshipCode ?? '',
    relationshipName: data?.relationshipName ?? '',
    selfReported: data?.selfReported ?? false,
    conditions: data?.conditions ?? [],
    associatedResources: [],
  };

  const famHisValidationSchema: Schema<Partial<FamilyHistoryValues>> =
    object().shape({
      dateOfBirth: string(),
      deceasedDate: dateNotBeforeSchema(
        'dateOfBirth',
        formatMessage(messages.deceasedBeforeBirth)
      ).nullable(),
      description: string(),
      isDeceased: boolean().nullable(),
      maternalPaternal: string(),
      relationshipCode: string(),
      relationshipName: string().required(formatMessage(messages.required)),
      selfReported: boolean().required(formatMessage(messages.required)),
      conditions: array().of(
        getFamilyConditionValidationSchema(formatMessage(messages.required))
      ),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = famHisValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) return;
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let famHis: FamilyHistoryRecord | null | undefined;

    try {
      if (data?.id) {
        const { data: updatedFamHis } = await updateFamilyHistory({
          input: {
            familymemberhistory_id: data.id,
            familyMemberHistory: data.getModelInput(values),
          },
        });
        famHis = updatedFamHis;
      } else {
        const { data } = await createFamilyHistory({
          input: {
            ...getFamilyHistoryTemplate(patient_id, values),
          },
        });
        famHis = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: famHis?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (famHis && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.FAMILY_HISTORY}${famHis.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const handleRelationshipChange = (
          value:
            | string
            | {
                code: string;
                display: string;
                text: string;
                category: CategoryType;
              }
        ) => {
          if (typeof value === 'string') {
            setFieldValue('relationshipName', value);
            setFieldValue('relationshipCode', 'other');
          } else {
            setFieldValue('relationshipName', value.text);
            setFieldValue('relationshipCode', value.code);
          }
        };

        const children = (
          <>
            {!isModifying && (
              <DrilldownItem
                label={formatMessage(messages.type)}
                readOnly
                value={capitalize(data?.relationship?.category ?? '')}
              />
            )}

            <DrilldownItem
              dateFormats={['date']}
              isFhir
              label={formatMessage(messages.dob)}
              name="dateOfBirth"
              onChange={(value) => {
                setFieldValue('dateOfBirth', value);
              }}
              type="date"
              value={values.dateOfBirth}
            />

            {(isModifying || values.deceasedDate) && (
              <DrilldownItem
                dateFormats={['date', 'year']}
                isFhir
                label={formatMessage(messages.deceasedDate)}
                name="deceasedDate"
                onChange={(value) => {
                  setFieldValue('deceasedDate', value);
                }}
                type="date"
                value={values.deceasedDate}
                max={true}
                min={values.dateOfBirth}
              />
            )}

            {(isModifying || !values.deceasedDate) && (
              <DrilldownItem
                headerLabel={
                  isModifying
                    ? 'If only the deceased status is known, check this field'
                    : formatMessage(messages.deceased)
                }
                label={formatMessage(messages.deceased)}
                name="isDeceased"
                type="checkbox"
                value={formatMessage(
                  values.isDeceased ? messages.yes : messages.no
                )}
              />
            )}

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.description)}
                name="description"
                type="text-area"
                value={values.description}
              />
            </ColumnLayout>

            <Typography color="text.secondary">
              {formatMessage(messages.conditions)}
            </Typography>
            <FieldArray
              name="conditions"
              render={(arrayHelpers) => {
                const condition: FamilyMemberCondition = {
                  name: '',
                  diagnosedYear: '',
                  ageDiagnosed: '',
                };
                if (!values.conditions.length) {
                  arrayHelpers.push({ ...condition });
                }

                return (
                  <>
                    {values.conditions.length > 0 &&
                      values.conditions.map((condition, index) => (
                        <Box
                          key={`family-condition-${index}`}
                          style={{
                            width: '100%',
                          }}
                        >
                          <FamilyConditionForm
                            arrayHelpers={arrayHelpers}
                            index={index}
                            value={condition}
                          />
                          {isModifying &&
                          index < values.conditions.length - 1 ? (
                            <Divider sx={{ my: 2 }} variant="middle" />
                          ) : null}
                        </Box>
                      ))}

                    {isModifying && (
                      <RoundedButton
                        autoSize
                        fontSize={13}
                        height={40}
                        iconName="plus"
                        onClick={() => arrayHelpers.push({ ...condition })}
                        type="button"
                      >
                        {formatMessage(messages.add)}
                      </RoundedButton>
                    )}
                  </>
                );
              }}
            />
            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />
            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps = {
          // TODO
          // actions={[
          //   {
          //     title: 'edit',
          //     handleClick: () => {
          //       navigate(`${ROUTES.EDIT_FAMILY_HISTORY}${id}`);
          //     },
          //   },
          //   {
          //     title: 'delete',
          //     handleClick: handleDelete,
          //   },
          // ]}
          loading,
          resource: data,
          selfReported: values.selfReported,
          title:
            values.relationshipName || formatMessage(messages.defaultTitle),
          titleInput: (
            <Autocomplete
              disableClearable
              freeSolo
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.text
              }
              onChange={(_event, value) => {
                handleRelationshipChange(value);
              }}
              options={SORTED_FAMILY_RELATIONSHIP_OPTIONS}
              renderInput={(params) => (
                <Field name="relationshipName">
                  {({ field, meta }: FieldProps<string>) => (
                    <TextField
                      {...params}
                      {...field}
                      error={Boolean(meta.error)}
                      helperText={meta.touched ? meta.error : ''}
                      margin="dense"
                      onChange={(event) => {
                        const { value } = event.target;
                        handleRelationshipChange(value);
                      }}
                      placeholder={formatMessage(messages.relationship)}
                      required
                      sx={{ fontSize: 20 }}
                    />
                  )}
                </Field>
              )}
              sx={{ width: 320, marginBottom: '10px' }}
              value={values.relationshipName ?? ''}
            />
          ),
        };

        return (
          <Form>
            <OneOfFieldWatcher fields={['isDeceased', 'deceasedDate']} />
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={ROUTES.FAMILY_HISTORY}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default FamilyHistory;
